import { forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import useAuthStore from "@/store/authStore";

/**
 * <PermButton entity="BuyerSKU" action="CREATE">…</PermButton>
 *
 * Drop-in replacement for the shadcn <Button> that automatically:
 *   1. Disables the button if the current user lacks the required RBAC permission.
 *   2. Greys it out visually (uses the existing disabled styles).
 *   3. Adds a Lock icon next to the label so the disabled state reads as
 *      "permission-locked" instead of "loading / error".
 *   4. Wraps in a tooltip explaining why ("Requires CREATE permission on BuyerSKU").
 *
 * MASTER_ADMIN bypasses every check (`hasPermission` already handles that).
 *
 * Forwarded ref so it composes with shadcn primitives that need ref access
 * (DropdownMenuTrigger, DialogTrigger, etc.).
 */
const PermButton = forwardRef(
  ({ entity, action, children, disabled, title, hideWhenLocked = false, ...rest }, ref) => {
    const { hasPermission } = useAuthStore();
    const allowed = !entity || !action || hasPermission(entity, action);

    // Optional: completely hide the button when locked (use case: pages where
    // showing a greyed button would clutter the UI). Default: show greyed.
    if (!allowed && hideWhenLocked) return null;

    const finalDisabled = disabled || !allowed;
    const lockMsg = !allowed
      ? `You need ${action} permission on ${entity} to use this.`
      : title;

    const inner = (
      <Button
        ref={ref}
        disabled={finalDisabled}
        title={lockMsg}
        data-permission-locked={!allowed || undefined}
        {...rest}
      >
        {!allowed && <Lock className="w-3 h-3 mr-1 opacity-70" />}
        {children}
      </Button>
    );

    // No tooltip when the user is allowed — keep the button behaving exactly
    // like a normal shadcn Button. Lock tooltip only when locked.
    if (allowed) return inner;

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          {/* span wrapper because disabled buttons don't fire mouse events */}
          <TooltipTrigger asChild>
            <span className="inline-flex">{inner}</span>
          </TooltipTrigger>
          <TooltipContent side="top">{lockMsg}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
);

PermButton.displayName = "PermButton";

export default PermButton;
